/**
 * AdhocProposalMessage — the chat "draft content" proposal, rendered
 * IN THE CHAT THREAD as Asthra's response message (tail block) while
 * the full-section draft awaits the writer's Approve/Reject.
 *
 * Thin adapter over ``ProposalCard`` — THE shared text-edit proposal
 * surface (also behind EditProposalMessage and WriteApprovalMessage).
 * This module only maps an ad-hoc draft proposal onto the card:
 *   - "Draft content" intent chip + header naming section+instruction
 *   - sanitized markdown preview (exactly what Approve will store in
 *     the SSOT and insert into Word — the approve path runs the same
 *     sanitizer once and submits that string to both)
 *   - drift warning after a version-conflict 409 (the writer edited
 *     the section after the draft was made; approving again is a
 *     deliberate overwrite)
 *
 * SRP: pure presentation. State machine + apply logic live in
 * useAdhocProposalFlow; on decision the shell appends the durable
 * ``adhoc_draft`` EventLog entry (this transient component then
 * unmounts and the projected message takes its place in history).
 */

import type React from "react";
import { useMemo } from "react";
import type { AdhocProposal, AdhocProposalPhase } from "../hooks/useAdhocProposalFlow";
import { intentLabel } from "../intentLabels";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import ApplyProgress, { type ApplyStep } from "./ApplyProgress";
import ProposalCard from "./ProposalCard";

interface AdhocProposalMessageProps {
  proposal: AdhocProposal;
  phase: AdhocProposalPhase;
  driftWarning: string | null;
  /** Post-approve apply checklist from useAdhocProposalFlow — non-empty
   *  only while an approve is being applied (empty for reject). */
  applySteps: ApplyStep[];
  onApprove: () => void;
  onReject: () => void;
}

export const AdhocProposalMessage: React.FC<AdhocProposalMessageProps> = ({
  proposal,
  phase,
  driftWarning,
  applySteps,
  onApprove,
  onReject,
}) => {
  const instructionPreview =
    proposal.instruction.length > 80 ? `${proposal.instruction.slice(0, 79)}…` : proposal.instruction;
  const isRedraft = proposal.mode === "redraft";
  const isEdit = proposal.mode === "edit";
  // "Redraft" is a literal: /regen is a command, not a routed intent,
  // so it has no intentLabel entry anymore.
  const chip = isRedraft ? "Redraft" : intentLabel(isEdit ? "edit_section" : "draft_content");
  const noun = isRedraft ? "redraft" : isEdit ? "edit" : "draft";
  const sectionLabel = proposal.sectionNumber ? `§${proposal.sectionNumber}` : proposal.sectionLabel;

  // While applying an approve, replace the card with the stepped checklist
  // so the writer sees the 15–30s Word render progress. Reject keeps the
  // plain busy card (applySteps is empty there).
  if (phase === "applying" && applySteps.length > 0) {
    return (
      <ApplyProgress
        steps={applySteps}
        chipText={chip}
        header={`Applying the ${noun} to ${sectionLabel}…`}
        ariaLabel={`Applying ${noun} to ${sectionLabel}`}
      />
    );
  }

  // Preview exactly what Approve will apply — the approve path runs the
  // same sanitizer before submitting to the SSOT + Word.
  const previewMarkdown = useMemo(() => sanitizeDraftForWord(proposal.content), [proposal.content]);

  return (
    <ProposalCard
      chipText={chip}
      header={`Proposed ${noun} for ${proposal.sectionLabel} — "${instructionPreview}"`}
      previewMarkdown={previewMarkdown}
      warning={driftWarning}
      metaChips={[proposal.sectionLabel, `${proposal.wordCount} words`]}
      busy={phase === "applying"}
      approveLabel={driftWarning ? "Approve & overwrite" : isRedraft || isEdit ? "Approve & replace" : "Approve & insert"}
      busyLabel="Applying…"
      ariaLabel={`Section ${noun} proposal for ${proposal.sectionLabel}`}
      onApprove={onApprove}
      onReject={onReject}
    />
  );
};

export default AdhocProposalMessage;
